import React from "react";
import Search from "../../../CommonComponents/Search";
import Table from "../../../CommonComponents/Table";
import { InputDropdownOption } from "../../../CommonComponents/InputDropdown";
import Button from "../../../Button";
import { useDebouncedValue } from "../../../../Hooks/useDebounceValue";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import useAuth from "../../../../Hooks/useAuth";
import Toast from "../../../CommonComponents/Toast";
import { libTypeIds, RESULT_STATUS } from "../../../../constants";

// --- Constants (defined once, never recreated) ---

const STATUS_MAP = {
  2: "Active",
  1: "Inactive",
  0: "Draft",
};

const TYPE_MAP = {
  All: "All",
  2: "MCQ",
  3: "Descriptive",
  5: "Coding",
  11: "SQL",
  12: "Coding Snippet",
  VIDEO_ASSESSMENT: "Video Assessment",
  AI_INTERVIEW: "AI Interview",
};

// Explicit ordered array so "All" is always first in the dropdown,
// regardless of how JS engines sort numeric vs string keys in objects.
const TYPE_FILTER_OPTIONS = [
  { key: "All", label: "All" },
  { key: "2", label: "MCQ" },
  { key: "3", label: "Descriptive" },
  { key: "5", label: "Coding" },
  { key: "11", label: "SQL" },
  { key: "12", label: "Coding Snippet" },
  { key: "VIDEO_ASSESSMENT", label: "Video Assessment" },
  { key: "AI_INTERVIEW", label: "AI Interview" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All Tests", value: "All" },
  { label: "Active Tests", value: "Active" },
  { label: "Inactive Tests", value: "Inactive" },
];

const MOCK_TYPE_FILTERS = [
  {
    label: "Company Specific",
    value: "COMPANY",
  },
  {
    label: "Tech Specific",
    value: "TECH",
  },
  // {
  //   label: "AI Interview",
  //   value: "AI_INTERVIEW",
  // },
  // {
  //   label: "Video Assessment",
  //   value: "VIDEO_ASSESSMENT",
  // },
];

const SCOPE_MOCK_TYPE_FILTERS = ["COMPANY", "TECH"];

const STATUS_QUERY_MAP = {
  Active: "2",
  Inactive: "1",
  Draft: "0",
};

// Checkmark icon extracted as a stable component to avoid JSX in data arrays
const CheckIcon = () => (
  <svg
    width="10"
    height="9"
    viewBox="0 0 10 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.95534 0.228697L2.98867 5.98703L1.40534 4.29536C1.11367 4.02036 0.655338 4.0037 0.322005 4.23703C-0.00299511 4.4787 -0.094662 4.9037 0.105338 5.24536L1.98034 8.29536C2.16367 8.5787 2.48034 8.7537 2.83867 8.7537C3.18034 8.7537 3.50534 8.5787 3.68867 8.29536C3.98867 7.9037 9.71367 1.0787 9.71367 1.0787C10.4637 0.31203 9.55534 -0.362969 8.95534 0.220364V0.228697Z"
      fill="#667085"
    />
  </svg>
);

// Stat icons as stable named components instead of inline JSX in data arrays
const ClipboardIcon = () => (
  <svg
    width="18"
    height="20"
    viewBox="0 0 18 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H6.2C6.41667 1.4 6.77917 0.916667 7.2875 0.55C7.79583 0.183333 8.36667 0 9 0C9.63333 0 10.2042 0.183333 10.7125 0.55C11.2208 0.916667 11.5833 1.4 11.8 2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V4H2V18ZM4 16H11V14H4V16ZM4 12H14V10H4V12ZM4 8H14V6H4V8ZM9 3.25C9.21667 3.25 9.39583 3.17917 9.5375 3.0375C9.67917 2.89583 9.75 2.71667 9.75 2.5C9.75 2.28333 9.67917 2.10417 9.5375 1.9625C9.39583 1.82083 9.21667 1.75 9 1.75C8.78333 1.75 8.60417 1.82083 8.4625 1.9625C8.32083 2.10417 8.25 2.28333 8.25 2.5C8.25 2.71667 8.32083 2.89583 8.4625 3.0375C8.60417 3.17917 8.78333 3.25 9 3.25ZM2 18V4V18Z"
      fill="#F55533"
    />
  </svg>
);

const RocketIcon = () => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.6 7.99556L5.55 8.82056C5.78333 8.3539 6.025 7.9039 6.275 7.47056C6.525 7.03723 6.8 6.6039 7.1 6.17056L5.7 5.89556L3.6 7.99556ZM7.15 10.0706L10 12.8956C10.7 12.6289 11.45 12.2206 12.25 11.6706C13.05 11.1206 13.8 10.4956 14.5 9.79556C15.6667 8.6289 16.5792 7.33306 17.2375 5.90806C17.8958 4.48306 18.1833 3.17056 18.1 1.97056C16.9 1.88723 15.5833 2.17473 14.15 2.83306C12.7167 3.4914 11.4167 4.4039 10.25 5.57056C9.55 6.27056 8.925 7.02056 8.375 7.82056C7.825 8.62056 7.41667 9.37056 7.15 10.0706ZM11.6 8.44556C11.2167 8.06223 11.025 7.5914 11.025 7.03306C11.025 6.47473 11.2167 6.0039 11.6 5.62056C11.9833 5.23723 12.4583 5.04556 13.025 5.04556C13.5917 5.04556 14.0667 5.23723 14.45 5.62056C14.8333 6.0039 15.025 6.47473 15.025 7.03306C15.025 7.5914 14.8333 8.06223 14.45 8.44556C14.0667 8.8289 13.5917 9.02056 13.025 9.02056C12.4583 9.02056 11.9833 8.8289 11.6 8.44556ZM12.075 16.4706L14.175 14.3706L13.9 12.9706C13.4667 13.2706 13.0333 13.5414 12.6 13.7831C12.1667 14.0247 11.7167 14.2622 11.25 14.4956L12.075 16.4706ZM19.9 0.145565C20.2167 2.16223 20.0208 4.12473 19.3125 6.03306C18.6042 7.9414 17.3833 9.76223 15.65 11.4956L16.15 13.9706C16.2167 14.3039 16.2 14.6289 16.1 14.9456C16 15.2622 15.8333 15.5372 15.6 15.7706L11.4 19.9706L9.3 15.0456L5.025 10.7706L0.1 8.67056L4.275 4.47056C4.50833 4.23723 4.7875 4.07056 5.1125 3.97056C5.4375 3.87056 5.76667 3.8539 6.1 3.92056L8.575 4.42056C10.3083 2.68723 12.125 1.46223 14.025 0.745565C15.925 0.0288979 17.8833 -0.171102 19.9 0.145565ZM1.875 13.9456C2.45833 13.3622 3.17083 13.0664 4.0125 13.0581C4.85417 13.0497 5.56667 13.3372 6.15 13.9206C6.73333 14.5039 7.02083 15.2164 7.0125 16.0581C7.00417 16.8997 6.70833 17.6122 6.125 18.1956C5.70833 18.6122 5.0125 18.9706 4.0375 19.2706C3.0625 19.5706 1.71667 19.8372 0 20.0706C0.233333 18.3539 0.5 17.0081 0.8 16.0331C1.1 15.0581 1.45833 14.3622 1.875 13.9456ZM3.3 15.3456C3.13333 15.5122 2.96667 15.8164 2.8 16.2581C2.63333 16.6997 2.51667 17.1456 2.45 17.5956C2.9 17.5289 3.34583 17.4164 3.7875 17.2581C4.22917 17.0997 4.53333 16.9372 4.7 16.7706C4.9 16.5706 5.00833 16.3289 5.025 16.0456C5.04167 15.7622 4.95 15.5206 4.75 15.3206C4.55 15.1206 4.30833 15.0247 4.025 15.0331C3.74167 15.0414 3.5 15.1456 3.3 15.3456Z"
      fill="#F55533"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    width="18"
    height="16"
    viewBox="0 0 18 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 10V8H7V10H0ZM0 6V4H11V6H0ZM0 2V0H11V2H0ZM9 16V12.925L14.525 7.425C14.675 7.275 14.8417 7.16667 15.025 7.1C15.2083 7.03333 15.3917 7 15.575 7C15.775 7 15.9667 7.0375 16.15 7.1125C16.3333 7.1875 16.5 7.3 16.65 7.45L17.575 8.375C17.7083 8.525 17.8125 8.69167 17.8875 8.875C17.9625 9.05833 18 9.24167 18 9.425C18 9.60833 17.9667 9.79583 17.9 9.9875C17.8333 10.1792 17.725 10.35 17.575 10.5L12.075 16H9ZM16.5 9.425L15.575 8.5L16.5 9.425ZM10.5 14.5H11.45L14.475 11.45L14.025 10.975L13.55 10.525L10.5 13.55V14.5ZM14.025 10.975L13.55 10.525L14.475 11.45L14.025 10.975Z"
      fill="#F55533"
    />
  </svg>
);

const ShapesIcon = () => (
  <svg
    width="19"
    height="20"
    viewBox="0 0 19 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.5 9L9 0L14.5 9H3.5ZM14.5 20C13.25 20 12.1875 19.5625 11.3125 18.6875C10.4375 17.8125 10 16.75 10 15.5C10 14.25 10.4375 13.1875 11.3125 12.3125C12.1875 11.4375 13.25 11 14.5 11C15.75 11 16.8125 11.4375 17.6875 12.3125C18.5625 13.1875 19 14.25 19 15.5C19 16.75 18.5625 17.8125 17.6875 18.6875C16.8125 19.5625 15.75 20 14.5 20ZM0 19.5V11.5H8V19.5H0ZM14.5 18C15.2 18 15.7917 17.7583 16.275 17.275C16.7583 16.7917 17 16.2 17 15.5C17 14.8 16.7583 14.2083 16.275 13.725C15.7917 13.2417 15.2 13 14.5 13C13.8 13 13.2083 13.2417 12.725 13.725C12.2417 14.2083 12 14.8 12 15.5C12 16.2 12.2417 16.7917 12.725 17.275C13.2083 17.7583 13.8 18 14.5 18ZM2 17.5H6V13.5H2V17.5ZM7.05 7H10.95L9 3.85L7.05 7Z"
      fill="#F55533"
    />
  </svg>
);

// Stable data array — icons are now component references, not inline JSX
const STATISTIC_CONFIGS = [
  {
    id: 1,
    Icon: ClipboardIcon,
    key: "total_count",
    title: "TOTAL MOCK TESTS",
    extra: "+12%",
    borderLeft: "4px solid #F55533",
  },
  {
    id: 2,
    Icon: RocketIcon,
    key: "active_count",
    title: "Active Tests",
    extra: "Live Now",
    borderLeft: "4px solid #12B76A",
  },
  {
    id: 3,
    Icon: EditIcon,
    key: "inactive_count",
    title: "DRAFT / INACTIVE",
    extra: "",
    borderLeft: "4px solid #F04438",
  },
  {
    id: 4,
    Icon: ShapesIcon,
    key: "topic_count",
    title: "TOTAL TOPICS",
    extra: "",
    borderLeft: "4px solid #4E5BA6",
  },
];

export const StatusBadge = ({
  value = 1,
  statusMap = STATUS_MAP,
  onClick = null,
}) => {
  return (
    <div
      className={`status-badge status-${statusMap[value]?.toLowerCase()}`}
      onClick={onClick && onClick}
    >
      {statusMap[value]}
    </div>
  );
};

// Stable column definition — defined outside so it's never recreated
const TABLE_COLUMNS = [
  { key: "title", title: "TEST NAME & DETAILS" },
  {
    key: "type",
    title: "TYPE",
    render: (col,row)=>{
        console.log("Rendering type column with col:", row);
        if (row?.aiMode) {
          return "AI Interview";
        } 

        if (col === 3 && row?.isVideoqns) {
          return "Video Assessment";
        }
        
        return TYPE_MAP[col] || col;
    },
  },
  { key: "topics", title: "TOPIC" },
  { key: "qncount", title: "NO. OF QUESTIONS" },
  { key: "duration", title: "DURATION" },
  {
    key: "mockuse",
    title: "STATUS",
    render: (col) => <StatusBadge value={col} statusMap={STATUS_MAP} />,
  },
];

// Memoized statistic card — prevents re-render when parent state changes
const MockTestStatistic = React.memo(({ item, count, extra }) => {
  const { Icon, borderLeft, title } = item;
  return (
    <div
      className="mock-test-management-util-top-statistic-container-item"
      style={{ borderLeft }}
    >
      <div className="mock-test-management-util-top-statistic-header">
        <Icon />
        <p
          className={`mock-test-management-util-top-statistic-header-label${
            extra ? " with-extra" : ""
          }`}
        >
          {extra}
        </p>
      </div>
      <div className="mock-test-management-util-top-statistic-content">
        <p className="mock-test-management-util-top-statistic-content-value">
          {count}
        </p>
        <p className="mock-test-management-util-top-statistic-content-label">
          {title}
        </p>
      </div>
    </div>
  );
});
MockTestStatistic.displayName = "MockTestStatistic";

// Reusable filter option icon — avoids recreating inline JSX in dropdowns
const FilterOptionIcon = ({ className }) => (
  <div className={`mock-test-management-util-filter-option-icon ${className}`}>
    <CheckIcon />
  </div>
);

const INITIAL_STATS = {
  total_count: 0,
  active_count: 0,
  inactive_count: 0,
  topic_count: 0,
  percentage: 0,
};

const MockTestList = () => {
  const axios = useAxiosPrivate();
  const { navigate, setIsLoaded, setShowNotify } = useAuth();

  const [stats, setStats] = React.useState(INITIAL_STATS);
  const [mockTestsData, setMockTestsData] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilterOpen, setStatusFilterOpen] = React.useState(false);
  const [typeFilterOpen, setTypeFilterOpen] = React.useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState("All");
  const [selectedTypeFilter, setSelectedTypeFilter] = React.useState("All");
  const [mockTypeFilter, setMockTypeFilter] = React.useState("All");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastType, setToastType] = React.useState("success");

  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    if (!statusFilterOpen && !typeFilterOpen) return;
    const handleOutsideClick = () => {
      setStatusFilterOpen(false);
      setTypeFilterOpen(false);
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [statusFilterOpen, typeFilterOpen]);

  React.useEffect(() => {
    const controller = new AbortController();

    const fetchMockTestsData = async () => {
      try {
        setIsLoaded(true);
        const statusQuery =
          selectedStatusFilter === "All"
            ? ""
            : `&status=${STATUS_QUERY_MAP[selectedStatusFilter] ?? ""}`;
        const libtypeidQuery =
          selectedTypeFilter === "All"||
          selectedTypeFilter === "VIDEO_ASSESSMENT" || selectedTypeFilter === "AI_INTERVIEW"
            ? ""
            : `&libtypeid=${selectedTypeFilter}`;

        const videoAssessmentTypeQuery =
          selectedTypeFilter === "VIDEO_ASSESSMENT"
            ? `&libtypeid=${libTypeIds.DESCRIPTIVE}&isVideoQns=true`
            : "";
        
        const aiInterviewTypeQuery = selectedTypeFilter === "AI_INTERVIEW" ? `&libtypeid=${libTypeIds.DESCRIPTIVE}&aiMode=true` : "";
          console.log("Hitted the validations  rg");
        const isScopeMockType = SCOPE_MOCK_TYPE_FILTERS.includes(mockTypeFilter);
        const shouldExcludeAssessmentModes =
          isScopeMockType && selectedTypeFilter !== "VIDEO_ASSESSMENT" &&  selectedTypeFilter !== "AI_INTERVIEW";;

        const mockTypeQuery = isScopeMockType
          ? `&scope=${mockTypeFilter}`
          : "";

        const videoAssessmentQuery =
          mockTypeFilter === "VIDEO_ASSESSMENT" ? `&libtypeid=${libTypeIds.DESCRIPTIVE}&isVideoQns=true` : "";
        const aiInterviewQuery =
          mockTypeFilter === "AI_INTERVIEW" ? `&libtypeid=${libTypeIds.DESCRIPTIVE}&aiMode=true` : "";
        const excludeAssessmentModeQuery = shouldExcludeAssessmentModes
          ? `&isVideoQns=false&aiMode=false`
          : "";

        const { data } = await axios.get(
          `node/admin/test?mockuse=true&search=${debouncedSearchQuery}${statusQuery}${libtypeidQuery}${videoAssessmentTypeQuery}${aiInterviewTypeQuery}${mockTypeQuery}${videoAssessmentQuery}${aiInterviewQuery}${excludeAssessmentModeQuery}`,
          {
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
          },
        );

        if (data?.resultCode === RESULT_STATUS.SUCCESS) {
          setStats({
            total_count: data?.total_count ?? 0,
            active_count: data?.active_count ?? 0,
            inactive_count: data?.inactive_count ?? 0,
            topic_count: data?.topic_count ?? 0,
            percentage: data?.percentage ?? 0,
          });

          const mockTests = JSON.parse(data?.data?.tests || "[]");
          setMockTestsData(
            mockTests.map((test) => ({
              ...test,
              topics: test.topics?.map((t) => t.topicname).join(", "),
              topicsList: test.topics,
            })),
          );
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.name === "AbortError")
          return;

        const isUnAuth = error?.response?.status === 403;
        setShowNotify({
          show: true,
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Failed to fetch mock tests data",
          title: isUnAuth ? "Warning !" : "Error !",
          isUnAuth,
        });
      } finally {
        setIsLoaded(false);
      }
    };

    fetchMockTestsData();
    return () => controller.abort();
  }, [
    axios,
    setIsLoaded,
    setShowNotify,
    debouncedSearchQuery,
    selectedStatusFilter,
    selectedTypeFilter,
    mockTypeFilter,
  ]);

  const onSelectTypeFilter = React.useCallback((value) => {
    setSelectedTypeFilter(value);
    setTypeFilterOpen(false);
  }, []);

  const onSelectStatusFilter = React.useCallback((value) => {
    setSelectedStatusFilter(value);
    setStatusFilterOpen(false);
  }, []);

  const onSelectMockTypeFilter = React.useCallback((value) => {
    setMockTypeFilter((prev) => (prev === value ? "All" : value));
  }, []);

  const handleSearchChange = React.useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleNavigateCreate = React.useCallback(() => {
    navigate("library/mock/test/create");
  }, [navigate]);

  const handleTypeFilterToggle = React.useCallback((e) => {
    e.stopPropagation();
    setStatusFilterOpen(false);
    setTypeFilterOpen((prev) => !prev);
  }, []);

  const handleStatusFilterToggle = React.useCallback((e) => {
    e.stopPropagation();
    setTypeFilterOpen(false);
    setStatusFilterOpen((prev) => !prev);
  }, []);

  const onChangeTestStatus = async (value, id) => {
    try {
      setIsLoaded(true);
      let mockValue = value === 1 ? 2 : 1;
      let response = await axios.put(
        `node/admin/test/settings/update/${id}`,
        JSON.stringify({ activateMock: mockValue }),
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
        setMockTestsData((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, mockuse: mockValue } : item,
          ),
        );
      } else {
        setShowNotify({
          title: "Warning !",
          msg:
            response.data.msg ??
            `Unable to change the status. Please try again later`,
          show: true,
        });
      }
    } catch (error) {
      console.error("error in change test status", error);
      const isUnAuth = error?.response?.status === 403;
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : error.message ??
            `Unable to change the status. Please try again later`,
        show: true,
      });
      // getCatchBlockDetails(error);
    } finally {
      setIsLoaded(false);
    }
  };

  return (
    <div className="mock-test-management-container">
      <div className="mock-test-management-wrapper">
        <div className="mock-test-management-utils-container">
          {/* Header + Statistics */}
          <div className="mock-test-management-util-top-container">
            <h2 className="mock-test-management-util-top-header">
              Mock Test Management
            </h2>
            <div className="mock-test-management-util-top-statistics-container">
              {STATISTIC_CONFIGS.map((item) => (
                <MockTestStatistic
                  key={item.id}
                  item={item}
                  count={stats[item.key]}
                  extra={
                    item.id === 1
                      ? stats.percentage
                      : item.id === 2
                      ? "LIVE NOW"
                      : ""
                  }
                />
              ))}
            </div>
          </div>

          {/* Filters + Actions */}
          <div className="mock-test-management-util-bottom-container">
            <div className="mock-test-management-util-bottom-section-1-container">
              <div className="mock-test-management-util-bottom-section-1-item">
                <Search
                  className="mock-test-management-util-search-field"
                  placeholder="Search tests, topics..."
                  width="320px"
                  height="44px"
                  onChange={handleSearchChange}
                />

                {/* Type filter */}
                <div
                  className="mock-test-management-util-type-filter mock-test-management-util-filter"
                  onClick={handleTypeFilterToggle}
                >
                  <svg
                    width="20"
                    height="14"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 7H16M1 1H19M7 13H13"
                      stroke="#344054"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Type : {TYPE_MAP[selectedTypeFilter] || "All"}
                  {typeFilterOpen && (
                    <div className="input-dropdown-menu mock-test-management-util-filter-options">
                      {TYPE_FILTER_OPTIONS.map(({ key, label }) => (
                        <InputDropdownOption
                          key={key}
                          label={label}
                          value={key}
                          className="mock-test-management-util-filter-option mock-test-management-util-type-filter-option"
                          onClick={onSelectTypeFilter}
                          selected={selectedTypeFilter === key}
                          icon={
                            <FilterOptionIcon className="mock-test-management-util-type-filter-option-icon" />
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Status filter */}
                <div
                  className="mock-test-management-util-status-filter mock-test-management-util-filter"
                  onClick={handleStatusFilterToggle}
                >
                  Status: {selectedStatusFilter}
                  {statusFilterOpen && (
                    <div className="input-dropdown-menu mock-test-management-util-filter-options">
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <InputDropdownOption
                          key={option.value}
                          label={option.label}
                          value={option.value}
                          selected={selectedStatusFilter === option.value}
                          onClick={onSelectStatusFilter}
                          className="mock-test-management-util-filter-option mock-test-management-util-status-filter-option"
                          icon={
                            <FilterOptionIcon className="mock-test-management-util-status-filter-option-icon" />
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Button
                size="sm"
                hierarchy={{ type: "primary", buttonText: "Create Mock Test" }}
                onClick={handleNavigateCreate}
              />
            </div>

            {/* Mock type tabs */}
            <div className="mock-test-management-util-bottom-section-2-container">
              {MOCK_TYPE_FILTERS.map((filter) => (
                <div
                  key={filter.label}
                  className={`mock-test-management-util-bottom-section-2-item${
                    mockTypeFilter === filter.value ? " active-type" : ""
                  }`}
                  onClick={() => onSelectMockTypeFilter(filter.value)}
                >
                  {filter.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Table
          className="mock-test-management-table"
          data={mockTestsData}
          columns={TABLE_COLUMNS}
          onColClick={(row, rowData, col) => {        
            if (col.key === "mockuse") {
              onChangeTestStatus(row.mockuse, row.id);
            } else {
              navigate(
                `library/mock/test/${row.id}${
                  row.isVideoqns ? "?isVideoQns=true" : ""
                }`,
              );
            }
          }}
          bordered
          stripped={false}
          fixedHeader
        />
      </div>

      <Toast
        open={toastOpen}
        message={toastMessage}
        type={toastType}
        onClose={() => setToastOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </div>
  );
};

export default MockTestList;
